import { z } from 'zod';
import { KEY_OPTIONS } from '../chords';
import { PLAN_ITEM_POSITIONS, PLAN_ITEM_TYPES, PLAN_TIME_KINDS, TONALITIES } from '../enums';
import { zId, zIdList, zIso, zOptId, zOptText, zText } from './common';

const zKey = z.enum(KEY_OPTIONS);

export const folderSchema = z.object({ name: zText(80), parentId: zOptId, campusId: zOptId });

export const serviceTypeSchema = z.object({
  name: zText(80),
  folderId: zOptId,
  campusId: zOptId,
  sortOrder: z.number().int().min(0).max(9999).default(0),
});

export const seriesSchema = z.object({ serviceTypeId: zId, title: zText(120), description: zOptText(2000) });

export const planTimeSchema = z
  .object({
    kind: z.enum(PLAN_TIME_KINDS).default('service'),
    name: zOptText(60),
    startsAt: zIso,
    endsAt: zIso.nullable().optional(),
  })
  .refine((t) => !t.endsAt || new Date(t.endsAt) > new Date(t.startsAt), {
    message: 'La hora de fin debe ser posterior al inicio',
    path: ['endsAt'],
  });

export const planCreateSchema = z.object({
  serviceTypeId: zId,
  title: zOptText(120),
  seriesId: zOptId,
  times: z.array(planTimeSchema).min(1, 'Agrega al menos un horario').max(20),
});
export const planUpdateSchema = z.object({ title: zOptText(120), seriesId: zOptId, publicNotes: zOptText(5000) });

export const planCopySchema = z.object({
  baseDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).min(1).max(52),
  copyItems: z.boolean().default(true),
  copyPeople: z.boolean().default(false),
  copyNeededPositions: z.boolean().default(true),
});

export const planItemSchema = z.object({
  itemType: z.enum(PLAN_ITEM_TYPES),
  title: zText(160),
  description: zOptText(2000),
  lengthSeconds: z.number().int().min(0).max(6 * 3600).default(0),
  songId: zOptId,
  arrangementId: zOptId,
  keyId: zOptId,
  mediaId: zOptId,
  /** Detalle del ítem: texto simple con saltos de línea; nunca se muestra como HTML. */
  htmlDetails: zOptText(20000),
  servicePosition: z.enum(PLAN_ITEM_POSITIONS).default('during'),
});
export const planItemUpdateSchema = planItemSchema.partial();

/** Nota de un ítem por categoría: el texto vacío la quita. */
export const itemNoteSchema = z.object({ category: zText(40), body: z.string().trim().max(10000) });
export const itemNoteCategoriesSchema = z.object({ names: z.array(zText(40)).max(30) });
export const itemLeaderSchema = z.object({ personId: zId });
export const itemMediaSchema = z.object({ mediaId: zId });

export const planItemReorderSchema = z.object({ sequence: zIdList.min(1) });
export const itemTimesSchema = z.object({ excludedTimeIds: zIdList });

export const serviceNoteSchema = z.object({ category: zText(40), body: zText(10000) });

export const teamSchema = z.object({
  serviceTypeId: zId,
  name: zText(80),
  scheduleTo: z.enum(['plan', 'time']).default('plan'),
  sequence: z.number().int().min(0).max(999).default(0),
});
export const teamPositionSchema = z.object({ name: zText(80), sequence: z.number().int().min(0).max(999).default(0) });
export const rosterSchema = z.object({ personId: zId });
export const teamLeaderSchema = z.object({ personId: zId });

export const schedulePersonSchema = z.object({
  personId: zId,
  teamId: zId,
  teamPositionId: zOptId,
  timeIds: zIdList.default([]),
  force: z.boolean().default(false),
});

export const respondSchema = z
  .object({
    status: z.enum(['C', 'D']),
    declinedTimeIds: zIdList.default([]),
    declineReason: zOptText(500),
  });

export const neededPositionSchema = z.object({
  teamId: zId,
  teamPositionId: zOptId,
  quantity: z.number().int().min(1).max(100),
});

export const blockoutSchema = z
  .object({
    personId: zOptId,
    startsAt: zIso,
    endsAt: zIso,
    reason: zOptText(200),
  })
  .refine((b) => new Date(b.endsAt) > new Date(b.startsAt), {
    message: 'El fin debe ser posterior al inicio',
    path: ['endsAt'],
  });

export const autoscheduleSchema = z.object({ teamIds: zIdList.min(1) });
export const notifySchema = z.object({ planPersonIds: zIdList.min(1) });

export const songSchema = z.object({
  title: zText(160),
  author: zOptText(200),
  copyright: zOptText(300),
  ccliNumber: zOptText(20),
  themes: zOptText(300),
  notes: zOptText(2000),
});

export const arrangementSchema = z.object({
  name: zText(80),
  bpm: z.number().int().min(20).max(400).nullable().optional(),
  meter: z.string().regex(/^\d{1,2}\/\d{1,2}$/, 'Compás inválido (ej. 4/4)').nullable().optional(),
  lengthSeconds: z.number().int().min(0).max(3600).default(0),
  lyrics: zOptText(50000),
  chordChart: zOptText(50000),
  chordChartKey: zKey.nullable().optional(),
  notes: zOptText(2000),
  /** Secuencia del arreglo, por ejemplo «V1, V2, C×2, E». */
  sequence: zOptText(500),
});

export const songKeySchema = z.object({
  name: zText(40),
  startingKey: zKey,
  endingKey: zKey.nullable().optional(),
  tonality: z.enum(TONALITIES).default('major'),
});

export const mediaSchema = z.object({
  title: zText(160),
  mediaType: z.enum(['video', 'image', 'audio', 'background', 'countdown', 'document']),
  url: z.string().url().max(2000).nullable().optional(),
  lengthSeconds: z.number().int().min(0).max(24 * 3600).default(0),
  themes: zOptText(300),
});
